import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import ConfirmationModal from '../common/ConfirmationModal';
import { Input } from '../ui/form';
import {
  SkeletonTableRow,
  SkeletonTableCell,
  SkeletonText,
  SkeletonTextShort,
  SkeletonBadge,
  Pagination,
  PaginationButton,
  PaginationInfo
} from '../ui';

interface User {
  _id: string;
  firstName?: string;
  lastName?: string;
  email: string;
  role?: 'user' | 'admin' | 'super_user';
  created_at: string;
  updated_at: string;
}

interface Contact {
  _id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  role?: string;
  location?: string;
  company_id?: string | {
    _id: string;
    name: string;
    industry?: string;
  };
  company?: {
    _id: string;
    name: string;
    industry?: string;
  };
  sales_rep_id?: string | {
    _id: string;
    firstName: string;
    lastName: string;
  };
  last_activity_at?: string;
  status_outcome?: 'positivo' | 'negativo' | 'in_corso';
  notes?: string;
  created_at: string;
  updated_at: string;
}

const Container = styled.div`
  max-width: 1800px;
  margin: 0 auto;
  padding: 12px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #e5e7eb;
`;

const BackButton = styled.button`
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 16px;
  color: #374151;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
  
  &:hover {
    background: #e5e7eb;
  }
`;

const Title = styled.h1`
  color: #1f2937;
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  background: ${props => props.variant === 'primary' ? '#667eea' : '#f3f4f6'};
  color: ${props => props.variant === 'primary' ? 'white' : '#374151'};
  border: 1px solid ${props => props.variant === 'primary' ? '#667eea' : '#d1d5db'};
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.variant === 'primary' ? '#5a67d8' : '#e5e7eb'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 30px;
`;

const MainContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
`;

// Removed unused FormGroup and FormLabel styled components

const FormInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &:disabled {
    background: #f9fafb;
    color: #6b7280;
  }
`;

const ContactsSection = styled.div`
  margin-top: 40px;
`;

const SectionTitle = styled.h2`
  color: #1f2937;
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 20px;
`;

const ContactsTable = styled.div`
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  --skeleton-line-height: 22.4px;
  --table-grid-columns: minmax(150px, 2fr) minmax(200px, 2fr) minmax(120px, 1.5fr) minmax(150px, 1.5fr) minmax(80px, 1fr) minmax(100px, 1fr);
  --table-grid-gap: 0.5rem;
  --table-row-padding: 1rem;
  --table-row-border: 1px solid #e9ecef;
  --table-row-background: white;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(150px, 2fr) minmax(200px, 2fr) minmax(120px, 1.5fr) minmax(150px, 1.5fr) minmax(80px, 1fr) minmax(100px, 1fr);
  gap: 0.5rem;
  background: #f8f9fa;
  padding: 1rem;
  border-bottom: 2px solid #e9ecef;
  font-weight: 600;
  color: #495057;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: minmax(150px, 2fr) minmax(200px, 2fr) minmax(120px, 1.5fr) minmax(150px, 1.5fr) minmax(80px, 1fr) minmax(100px, 1fr);
  gap: 0.5rem;
  padding: 1rem;
  border-bottom: 1px solid #e9ecef;
  transition: background-color 0.2s;
  cursor: pointer;
  background: white;
  align-items: center;
  
  &:hover {
    background: #f8f9fa;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.div`
  display: flex;
  align-items: center;
  color: #1f2937;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  position: relative;
  
  &:hover::after {
    content: attr(title);
    position: absolute;
    bottom: 100%;
    left: 0;
    background: #1f2937;
    color: white;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 12px;
    white-space: normal;
    z-index: 1000;
    max-width: 300px;
    word-wrap: break-word;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    display: ${props => props.title && props.title.length > 20 ? 'block' : 'none'};
  }
  
  &:hover::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 10px;
    margin-bottom: -5px;
    border: 5px solid transparent;
    border-top-color: #1f2937;
    z-index: 1001;
    display: ${props => props.title && props.title.length > 20 ? 'block' : 'none'};
  }
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: capitalize;
  background: ${props => {
    switch (props.status) {
      case 'nuovo': return '#f5f5f5';
      case 'positivo': return '#e8f5e8';
      case 'negativo': return '#ffebee';
      case 'in_corso': return '#fff9c4';
      default: return '#f1f5f9';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'nuovo': return '#666';
      case 'positivo': return '#2e7d32';
      case 'negativo': return '#c62828';
      case 'in_corso': return '#f57f17';
      default: return '#475569';
    }
  }};
`;

const RoleBadge = styled.span<{ role: string }>`
  background: ${props => {
    switch (props.role) {
      case 'super_user': return '#8b5cf6';
      case 'admin': return '#3b82f6';
      case 'user': return '#10b981';
      default: return '#6b7280';
    }
  }};
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #6b7280;
  font-size: 16px;
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
`;

const SuccessMessage = styled.div`
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #16a34a;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
  --skeleton-line-height: 25.6px;
`;

const InfoCard = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
`;

const InfoLabel = styled.div`
  color: #6b7280;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`;

const InfoValue = styled.div`
  color: #1f2937;
  font-size: 16px;
  font-weight: 600;
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: #667eea;
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  
  &:hover {
    color: #5a67d8;
  }
`;

const getStatusLabel = (status: string | undefined): string => {
  switch (status) {
    case 'nuovo': return 'Nuovo';
    case 'positivo': return 'Positivo';
    case 'negativo': return 'Negativo';
    case 'in_corso': return 'In Corso';
    default: return 'Nuovo';
  }
};

const UserDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasRole, canCreateRole } = useAuth();

  // Helper to get initial state from URL
  const getInitialParam = (key: string, defaultVal: string = '') => {
    return new URLSearchParams(location.search).get(key) || defaultVal;
  };

  const [user, setUser] = useState<User | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [contactsSearchTerm, setContactsSearchTerm] = useState(() => getInitialParam('contactsSearch'));
  const [currentPage, setCurrentPage] = useState(() => {
    const p = parseInt(getInitialParam('contactsPage', '1'), 10);
    return isNaN(p) || p < 1 ? 1 : p;
  });
  const [totalPages, setTotalPages] = useState(1);
  const [totalContacts, setTotalContacts] = useState(0);
  const pageSize = 10;
  const expectedRowCount = totalContacts > 0
    ? Math.min(pageSize, Math.max(totalContacts - (currentPage - 1) * pageSize, 0))
    : 0;
  const skeletonRowCount = expectedRowCount > 0
    ? expectedRowCount
    : (contacts.length > 0 ? contacts.length : pageSize);
  const pendingScrollY = useRef<number | null>(null);
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'user' as 'user' | 'admin' | 'super_user'
  });

  // Scroll to top when entering detail page
  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    fetchUser();
  }, [id]);

  useEffect(() => {
    fetchUserContacts();
  }, [id, currentPage, contactsSearchTerm]);

  useEffect(() => {
    if (!contactsLoading && pendingScrollY.current !== null) {
      window.scrollTo({ top: pendingScrollY.current, behavior: 'auto' });
      pendingScrollY.current = null;
    }
  }, [contactsLoading]);

  // Sync sub-table state to URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (contactsSearchTerm) { params.set('contactsSearch', contactsSearchTerm); } else { params.delete('contactsSearch'); }
    if (currentPage > 1) { params.set('contactsPage', currentPage.toString()); } else { params.delete('contactsPage'); }
    navigate(`?${params.toString()}`, { replace: true });
  }, [contactsSearchTerm, currentPage]);

  const fetchUser = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/users/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data?.user) {
          const userData = result.data.user;
          setUser(userData);
          setFormData({
            firstName: userData.firstName || '',
            lastName: userData.lastName || '',
            email: userData.email || '',
            role: userData.role || 'user'
          });
        } else {
          setError('Invalid response format');
        }
      } else {
        setError('Failed to load user details');
      }
    } catch (error) {
      setError('An error occurred while loading user details');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserContacts = async () => {
    try {
      setContactsLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        sales_rep_id: id || '',
        page: currentPage.toString(),
        limit: pageSize.toString()
      });
      if (contactsSearchTerm) {
        params.append('search', contactsSearchTerm);
      }
      
      const response = await fetch(`/api/contacts?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data?.contacts) {
          setContacts(result.data.contacts);
          // Handle pagination data if available, otherwise default
          setTotalPages(result.data.totalPages || result.data.pagination?.totalPages || 1);
          setTotalContacts(result.data.totalContacts || result.data.pagination?.totalContacts || result.data.pagination?.total || result.data.contacts.length);
        } else {
          setContacts([]);
          setTotalPages(1);
          setTotalContacts(0);
        }
      } else {
        console.error('Failed to load user contacts');
      }
    } catch (error) {
      console.error('An error occurred while loading user contacts');
    } finally {
      setContactsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setHasChanges(true);
  };

  const handlePageChange = (nextPage: number) => {
    pendingScrollY.current = window.scrollY;
    setCurrentPage(nextPage);
  };

  const handleSave = async () => {
    try {
      setError('');
      setSuccess('');
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/users/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSuccess('Utente aggiornato con successo');
        setIsEditing(false);
        setHasChanges(false);
        fetchUser();
      } else {
        const data = await response.json();
        setError(data.message || 'Impossibile aggiornare l\'utente');
      }
    } catch (error) {
      setError('Si è verificato un errore durante l\'aggiornamento dell\'utente');
    }
  };

  const handleCancel = () => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        role: user.role || 'user'
      });
    }
    setIsEditing(false);
    setHasChanges(false);
    setError('');
    setSuccess('');
  };

  const handleDeleteUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/users/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setSuccess('Utente eliminato con successo');
        setTimeout(() => {
          navigate('/dashboard/users');
        }, 1500);
      } else {
        const result = await response.json();
        setError(result.message || 'Impossibile eliminare l\'utente');
      }
    } catch (error) {
      setError('Si è verificato un errore durante l\'eliminazione dell\'utente');
    } finally {
      setShowDeleteModal(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCompanyName = (contact: Contact) => {
    // Check if company data is populated from backend aggregation
    if (contact.company && contact.company.name) {
      return contact.company.name;
    }
    // Fallback to company_id object (for backward compatibility)
    if (typeof contact.company_id === 'object' && contact.company_id) {
      return contact.company_id.name;
    }
    return 'Not specified';
  };

  const getCompanyId = (contact: Contact): string | undefined => {
    // Check if company data is populated from backend aggregation
    if (contact.company && contact.company._id) {
      return contact.company._id;
    }
    // Fallback to company_id object (for backward compatibility)
    if (typeof contact.company_id === 'object' && contact.company_id) {
      return contact.company_id._id;
    }
    if (typeof contact.company_id === 'string') {
      return contact.company_id;
    }
    return undefined;
  };

  const canEditUser = hasRole(['admin', 'super_user']);
  const displayName = user?.firstName && user?.lastName 
    ? `${user.firstName} ${user.lastName}` 
    : user?.email || '';
  const isInitialLoading = loading && !user;

  return (
    <Container>
      {!loading && !user && (
        <ErrorMessage>
          Utente non trovato
        </ErrorMessage>
      )}

      {(loading || user) && (
        <>
          <Header>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <BackButton onClick={() => navigate(-1)}>
            ⬅️ Torna alla lista commerciali
          </BackButton>
        </div>
        {user && canEditUser && (
          <ActionButtons>
            {isEditing ? (
              <>
                <ActionButton onClick={handleCancel}>
                  ❌ Annulla
                </ActionButton>
                <ActionButton 
                  variant="primary" 
                  onClick={handleSave}
                  disabled={!hasChanges}
                >
                  💾 Salva Modifiche
                </ActionButton>
              </>
            ) : (
              <>
                <ActionButton 
                  variant="primary" 
                  onClick={() => setIsEditing(true)}
                >
                  ✏️ Modifica Utente
                </ActionButton>
                <ActionButton 
                  onClick={() => setShowDeleteModal(true)}
                  style={{ background: '#dc2626', color: 'white', borderColor: '#dc2626' }}
                >
                  🗑️ Elimina Utente
                </ActionButton>
              </>
            )}
          </ActionButtons>
        )}
          </Header>
      
          <div style={{ borderBottom: '1px solid #e5e7eb', margin: '0 0 20px 0' }}></div>
      
          <Title style={{ marginBottom: '20px' }}>
            {isInitialLoading ? <SkeletonText /> : (displayName || '—')}
          </Title>

      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}

          <ContentGrid>
        <MainContent>
          <InfoGrid>
            <InfoCard>
              <InfoLabel>Nome</InfoLabel>
              {isEditing ? (
                <FormInput
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="Inserisci nome"
                />
              ) : (
                isInitialLoading ? <SkeletonTextShort /> : <InfoValue>{user?.firstName || 'Non specificato'}</InfoValue>
              )}
            </InfoCard>
            
            <InfoCard>
              <InfoLabel>Cognome</InfoLabel>
              {isEditing ? (
                <FormInput
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="Inserisci cognome"
                />
              ) : (
                isInitialLoading ? <SkeletonTextShort /> : <InfoValue>{user?.lastName || 'Non specificato'}</InfoValue>
              )}
            </InfoCard>
            
            <InfoCard>
              <InfoLabel>Email</InfoLabel>
              {isEditing ? (
                <FormInput
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Inserisci indirizzo email"
                />
              ) : (
                isInitialLoading ? (
                  <SkeletonTextShort />
                ) : (
                  <InfoValue>
                    {user?.email ? (
                      <a 
                        href={`mailto:${user.email}`} 
                        style={{ color: '#667eea', textDecoration: 'none' }}
                      >
                        {user.email}
                      </a>
                    ) : (
                      'Non specificato'
                    )}
                  </InfoValue>
                )
              )}
            </InfoCard>
            
            <InfoCard>
              <InfoLabel>Ruolo</InfoLabel>
              {isEditing ? (
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: 'white'
                  }}
                >
                  <option value="user">Utente</option>
                  {canCreateRole('admin') && (
                    <option value="admin">Amministratore</option>
                  )}
                  {canCreateRole('super_user') && (
                    <option value="super_user">Super Utente</option>
                  )}
                </select>
              ) : (
                isInitialLoading ? (
                  <SkeletonTextShort />
                ) : (
                  <InfoValue>
                    <RoleBadge role={user?.role || 'user'}>
                      {(user?.role || 'user').replace('_', ' ')}
                    </RoleBadge>
                  </InfoValue>
                )
              )}
            </InfoCard>
            
            <InfoCard>
              <InfoLabel>Creato il</InfoLabel>
              {isInitialLoading ? <SkeletonTextShort /> : <InfoValue>{user ? formatDate(user.created_at) : '—'}</InfoValue>}
            </InfoCard>
            
            <InfoCard>
              <InfoLabel>Ultimo Aggiornamento</InfoLabel>
              {isInitialLoading ? <SkeletonTextShort /> : <InfoValue>{user ? formatDate(user.updated_at) : '—'}</InfoValue>}
            </InfoCard>
          </InfoGrid>
        </MainContent>
          </ContentGrid>

          <ContactsSection>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <SectionTitle style={{ marginBottom: 0 }}>Contatti Assegnati ({totalContacts})</SectionTitle>
          <Input 
            type="text" 
            placeholder="Cerca contatti..." 
            value={contactsSearchTerm}
            onChange={(e) => {
              setContactsSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            style={{ maxWidth: '300px' }}
          />
        </div>
        <ContactsTable>
          <TableHeader>
            <div>Nome</div>
            <div>Email</div>
            <div>Ruolo</div>
            <div>Azienda</div>
            <div>Stato</div>
            <div>Creato il</div>
          </TableHeader>
          {contactsLoading ? (
            Array.from({ length: skeletonRowCount }).map((_, index) => (
              <SkeletonTableRow key={index}>
                <SkeletonTableCell><SkeletonText /></SkeletonTableCell>
                <SkeletonTableCell><SkeletonTextShort /></SkeletonTableCell>
                <SkeletonTableCell><SkeletonTextShort /></SkeletonTableCell>
                <SkeletonTableCell><SkeletonTextShort /></SkeletonTableCell>
                <SkeletonTableCell><SkeletonBadge /></SkeletonTableCell>
                <SkeletonTableCell><SkeletonTextShort /></SkeletonTableCell>
              </SkeletonTableRow>
            ))
          ) : contacts.length === 0 ? (
            <EmptyState>
              Nessun contatto assegnato a questo utente.
            </EmptyState>
          ) : (
            contacts.map(contact => (
              <TableRow 
                key={contact._id} 
                onClick={() => navigate(`/dashboard/contacts/${contact._id}`)}
              >
                <TableCell title={`${contact.first_name} ${contact.last_name}`}>
                  {contact.first_name} {contact.last_name}
                </TableCell>
                <TableCell title={contact.email}>
                  <a 
                    href={`mailto:${contact.email}`} 
                    style={{ color: '#667eea', textDecoration: 'none' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {contact.email}
                  </a>
                </TableCell>
                <TableCell title={contact.role || 'Non specificato'}>
                  {contact.role || 'Non specificato'}
                </TableCell>
                <TableCell title={contact.company_id ? getCompanyName(contact) : 'Non specificato'}>
                  {contact.company_id ? (
                    <LinkButton 
                      onClick={(e) => {
                        e.stopPropagation();
                        const companyId = getCompanyId(contact);
                        if (companyId) navigate(`/dashboard/companies/${companyId}`);
                      }}
                    >
                      {getCompanyName(contact)}
                    </LinkButton>
                  ) : (
                    'Non specificato'
                  )}
                </TableCell>
                <TableCell title={getStatusLabel(contact.status_outcome)}>
                  <StatusBadge status={contact.status_outcome || 'nuovo'}>
                    {getStatusLabel(contact.status_outcome)}
                  </StatusBadge>
                </TableCell>
                <TableCell title={formatDate(contact.created_at)}>
                  {formatDate(contact.created_at)}
                </TableCell>
              </TableRow>
            ))
          )}
        </ContactsTable>

        <Pagination>
          <PaginationButton
            disabled={contactsLoading || currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            Precedente
          </PaginationButton>
          <PaginationInfo>
            Pagina {currentPage} di {totalPages} ({totalContacts} contatti)
          </PaginationInfo>
          <PaginationButton
            disabled={contactsLoading || currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            Successivo
          </PaginationButton>
        </Pagination>
          </ContactsSection>

          {user && (
            <ConfirmationModal
              isOpen={showDeleteModal}
              title="Elimina Utente"
              message={`Sei sicuro di voler eliminare ${displayName}? Questa azione eliminerà logicamente l'utente e non apparirà più nel sistema. Questa azione può essere annullata da un amministratore.`}
              confirmText="Elimina Utente"
              cancelText="Annulla"
              onConfirm={handleDeleteUser}
              onCancel={() => setShowDeleteModal(false)}
              variant="danger"
            />
          )}
        </>
      )}
    </Container>
  );
};

export default UserDetails;
